import DashboardLayout from "@/components/DashboardLayout";
import { ContinuousFilmPlayer } from "@/components/ContinuousFilmPlayer";
import { FilmTimeline } from "@/components/FilmTimeline";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDuration, formatInTz } from "@/lib/format";
import { decodeAutoPlaylist } from "@/lib/playlist";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Clapperboard, Download, Eraser, Film, Loader2, RefreshCcw, Scissors, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const WEEKDAYS = ["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"];
const MONTHS = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];

export default function FilmsPage() {
  const { user, isAdmin } = useAuth();
  const qc = useQueryClient();
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list() });
  const { data: cameras } = useQuery({ queryKey: ["cameras"], queryFn: () => api.cameras.list() });

  const [projectId, setProjectId] = useState("");
  const [cameraId, setCameraId] = useState("");
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedFilmId, setSelectedFilmId] = useState(null);
  const [rangeOpen, setRangeOpen] = useState(false);

  const project = useMemo(() => projects?.find((p) => p.id === Number(projectId)), [projects, projectId]);
  const tz = project?.timezone || "Europe/Bucharest";
  const filteredCameras = useMemo(() => (cameras || []).filter((c) => !projectId || c.projectId === Number(projectId)), [cameras, projectId]);

  const calParams = { year: calYear, month: calMonth, projectId: projectId ? Number(projectId) : undefined, cameraId: cameraId ? Number(cameraId) : undefined };
  const { data: calendar } = useQuery({ queryKey: ["films-calendar", calParams], queryFn: () => api.films.calendar(calParams) });

  const filmsParams = { projectId: projectId ? Number(projectId) : undefined, cameraId: cameraId ? Number(cameraId) : undefined, filmDate: selectedDate || undefined };
  const { data: films } = useQuery({ queryKey: ["films", filmsParams], queryFn: () => api.films.list(filmsParams) });

  useEffect(() => {
    if (selectedDate && films && films.length && !films.some((f) => f.id === selectedFilmId)) setSelectedFilmId(films[0].id);
  }, [films, selectedDate, selectedFilmId]);

  const selectedFilm = useMemo(() => films?.find((f) => f.id === selectedFilmId), [films, selectedFilmId]);
  const filmCamera = useMemo(() => cameras?.find((c) => c.id === selectedFilm?.cameraId), [cameras, selectedFilm]);

  const inval = () => { qc.invalidateQueries({ queryKey: ["films"] }); qc.invalidateQueries({ queryKey: ["films-calendar"] }); qc.invalidateQueries({ queryKey: ["stats"] }); };
  const regenMut = useMutation({ mutationFn: api.films.regenerate, onSuccess: () => { inval(); toast.success("Film regenerat"); }, onError: (e) => toast.error(apiError(e)) });
  const bulkMut = useMutation({ mutationFn: api.films.bulkRegenerate, onSuccess: (r) => { inval(); toast.success(`${r.regenerated} filme regenerate`); }, onError: (e) => toast.error(apiError(e)) });
  const deleteMut = useMutation({ mutationFn: (id) => api.films.remove(id), onSuccess: () => { inval(); setSelectedFilmId(null); toast.success("Film șters"); }, onError: (e) => toast.error(apiError(e)) });
  const rangeMut = useMutation({ mutationFn: api.films.generateRange, onSuccess: (f) => { inval(); setRangeOpen(false); setSelectedDate(f.filmDate); setSelectedFilmId(f.id); toast.success("Extras creat"); }, onError: (e) => toast.error(apiError(e)) });
  const purgeMut = useMutation({ mutationFn: api.clips.purgeUnavailable, onSuccess: (r) => { inval(); qc.invalidateQueries({ queryKey: ["clips"] }); toast.success(`${r.removed} clipuri indisponibile eliminate · ${r.filmsRegenerated} filme regenerate`); }, onError: (e) => toast.error(apiError(e)) });

  const [concatLoading, setConcatLoading] = useState(false);
  const downloadFilm = async (film) => {
    setConcatLoading(true);
    try {
      const res = await api.films.concatenate(film.id);
      if (res.url && (res.url.startsWith("http") || res.url.startsWith("/api"))) {
        const url = res.url.startsWith("http") ? res.url : `${process.env.REACT_APP_BACKEND_URL}${res.url}`;
        const a = document.createElement("a"); a.href = url; a.download = `${film.title}.mp4`; a.target = "_blank"; a.click();
        toast.success("Descărcare pornită");
      } else { toast.error("Fișier indisponibil pentru descărcare"); }
    } catch (e) { toast.error(apiError(e)); } finally { setConcatLoading(false); }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6" data-testid="films-page">
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">Filme zilnice</h1>
            <p className="text-sm text-muted-foreground mt-1">Un film continuu per cameră/zi, cu redare fără întreruperi și linie temporală cu markere de evenimente.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && projectId && cameraId && (
              <>
                <Button variant="outline" className="bg-background" onClick={() => bulkMut.mutate({ projectId: Number(projectId), cameraId: Number(cameraId) })} disabled={bulkMut.isPending}>{bulkMut.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1" />}Regenerează tot</Button>
                <Button variant="outline" className="bg-background" onClick={() => setRangeOpen(true)}><Scissors className="h-4 w-4 mr-1" />Extrage interval</Button>
                <Button variant="outline" className="bg-background text-destructive hover:text-destructive" onClick={() => { if (confirm("Elimină clipurile vechi indisponibile (fișiere netransferabile din sistemul Manus) pentru această cameră? Filmele vor rămâne doar cu clipurile redabile.")) purgeMut.mutate({ projectId: Number(projectId), cameraId: Number(cameraId) }); }} disabled={purgeMut.isPending}>{purgeMut.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eraser className="h-4 w-4 mr-1" />}Curăță indisponibile</Button>
              </>
            )}
          </div>
        </header>

        <Card>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-6">
            <div className="grid gap-1.5"><Label>Proiect</Label>
              <Select value={projectId || "all"} onValueChange={(v) => { const val = v === "all" ? "" : v; setProjectId(val); setCameraId(""); setSelectedDate(null); setSelectedFilmId(null); }}>
                <SelectTrigger data-testid="films-project-select"><SelectValue placeholder="Toate proiectele" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Toate proiectele</SelectItem>{projects?.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid gap-1.5"><Label>Cameră</Label>
              <Select value={cameraId || "all"} onValueChange={(v) => { setCameraId(v === "all" ? "" : v); setSelectedDate(null); setSelectedFilmId(null); }} disabled={!projectId}>
                <SelectTrigger data-testid="films-camera-select"><SelectValue placeholder="Toate camerele" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Toate camerele</SelectItem>{filteredCameras.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </CardContent>
        </Card>

        <Tabs defaultValue="calendar">
          <TabsList><TabsTrigger value="calendar" data-testid="tab-calendar">Calendar</TabsTrigger><TabsTrigger value="list" data-testid="tab-list">Listă</TabsTrigger></TabsList>

          <TabsContent value="calendar" className="mt-4">
            <CalendarGrid calYear={calYear} calMonth={calMonth} calendar={calendar || []} selectedDate={selectedDate}
              onPrev={() => { const d = new Date(calYear, calMonth - 2, 1); setCalYear(d.getFullYear()); setCalMonth(d.getMonth() + 1); }}
              onNext={() => { const d = new Date(calYear, calMonth, 1); setCalYear(d.getFullYear()); setCalMonth(d.getMonth() + 1); }}
              onSelect={(date, firstFilmId) => { setSelectedDate(date); setSelectedFilmId(firstFilmId); }} />
          </TabsContent>

          <TabsContent value="list" className="mt-4">
            <FilmsList films={films} cameras={cameras} tz={tz} selectedFilmId={selectedFilmId} onSelect={(f) => { setSelectedDate(f.filmDate); setSelectedFilmId(f.id); }} />
          </TabsContent>
        </Tabs>

        {selectedFilm && (
          <FilmPlayerBlock key={selectedFilm.id} film={selectedFilm} camera={filmCamera} tz={tz} isAdmin={isAdmin}
            canDownload={isAdmin || user?.canDownload} canDelete={isAdmin || user?.canDelete}
            concatLoading={concatLoading} onDownload={() => downloadFilm(selectedFilm)}
            onRegenerate={() => regenMut.mutate({ projectId: selectedFilm.projectId, cameraId: selectedFilm.cameraId, filmDate: selectedFilm.filmDate })}
            onDelete={() => { if (confirm("Ștergi acest film?")) deleteMut.mutate(selectedFilm.id); }} />
        )}
      </div>

      <RangeDialog open={rangeOpen} setOpen={setRangeOpen} projectId={projectId} cameraId={cameraId} tz={tz} onSubmit={(b) => rangeMut.mutate(b)} pending={rangeMut.isPending} />
    </DashboardLayout>
  );
}

function CalendarGrid({ calYear, calMonth, calendar, selectedDate, onPrev, onNext, onSelect }) {
  const byDate = useMemo(() => { const m = new Map(); calendar.forEach((c) => m.set(c.date, c)); return m; }, [calendar]);
  const firstDay = new Date(calYear, calMonth - 1, 1);
  const startWeekday = (firstDay.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(calYear, calMonth, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{MONTHS[calMonth - 1]} {calYear}</CardTitle>
        <div className="flex gap-1"><Button size="icon" variant="outline" onClick={onPrev} className="bg-background h-8 w-8"><ChevronLeft className="h-4 w-4" /></Button><Button size="icon" variant="outline" onClick={onNext} className="bg-background h-8 w-8"><ChevronRight className="h-4 w-4" /></Button></div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-1.5 mb-2">{WEEKDAYS.map((w) => <div key={w} className="text-center text-xs font-medium text-muted-foreground py-1">{w}</div>)}</div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((d, i) => {
            if (d === null) return <div key={`e${i}`} />;
            const dateStr = `${calYear}-${String(calMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            const info = byDate.get(dateStr);
            const isSelected = selectedDate === dateStr;
            return (
              <button key={dateStr} disabled={!info} data-testid={info ? `calendar-day-${dateStr}` : undefined}
                onClick={() => info && onSelect(dateStr, info.firstFilmId)}
                className={`aspect-square rounded-lg border flex flex-col items-center justify-center gap-0.5 text-sm transition-colors ${
                  isSelected ? "border-primary bg-primary text-primary-foreground" : info ? "border-primary/40 bg-primary/5 hover:bg-primary/15 cursor-pointer" : "border-transparent text-muted-foreground/40"}`}>
                <span className="font-medium">{d}</span>
                {info && <span className={`text-[9px] leading-none px-1 rounded-full ${isSelected ? "bg-white/25" : "bg-primary/15 text-primary"}`}>{info.count}f</span>}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function FilmsList({ films, cameras, tz, selectedFilmId, onSelect }) {
  if (!films?.length) return <Card><CardContent className="py-12 flex flex-col items-center text-center gap-3"><Film className="h-10 w-10 text-muted-foreground" /><p className="text-sm text-muted-foreground max-w-md">Niciun film pentru filtrul curent. Selectați o zi din calendar sau încărcați clipuri pentru a genera filme zilnice.</p></CardContent></Card>;
  return (
    <Card><CardContent className="p-0"><ul className="divide-y divide-border">
      {films.map((f) => {
        const cam = cameras?.find((c) => c.id === f.cameraId);
        return (
          <li key={f.id}>
            <button onClick={() => onSelect(f)} className={`w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-muted/50 transition-colors ${selectedFilmId === f.id ? "bg-muted/60" : ""}`}>
              <div className="min-w-0"><p className="text-sm font-medium truncate">{f.title}</p><p className="text-xs text-muted-foreground truncate">{cam?.name ?? `Cameră #${f.cameraId}`} · {f.filmDate} · {formatDuration(f.durationSec)} · {f.clipsCount} clipuri</p></div>
              <Badge variant={f.isAuto ? "secondary" : "default"} className="shrink-0">{f.isAuto ? "AUTO" : "MANUAL"}</Badge>
            </button>
          </li>
        );
      })}
    </ul></CardContent></Card>
  );
}

function FilmPlayerBlock({ film, camera, tz, isAdmin, canDownload, canDelete, concatLoading, onDownload, onRegenerate, onDelete }) {
  const playlist = useMemo(() => decodeAutoPlaylist(film.fileUrl) || [], [film.fileUrl]);
  const [activeIndex, setActiveIndex] = useState(0);
  const playerRef = useRef(null);
  useEffect(() => { setActiveIndex(0); }, [film.id]);

  const { data: rawEvents } = useQuery({ queryKey: ["film-events", film.id], queryFn: () => api.films.events(film.id) });
  const events = useMemo(() => (rawEvents || []).map((e) => ({ id: e.id, category: e.category, notes: e.notes, filmOffsetSec: e.filmOffsetSec, startedAtMs: Date.parse(e.startedAt) })), [rawEvents]);

  const cumulative = useMemo(() => { const arr = []; let sum = 0; playlist.forEach((c) => { arr.push(sum); sum += c.durationSec || 0; }); return arr; }, [playlist]);

  const seekToOffset = (offsetSec) => {
    let idx = 0;
    for (let i = 0; i < playlist.length; i++) { if (cumulative[i] <= offsetSec) idx = i; else break; }
    const within = Math.max(0, offsetSec - cumulative[idx]);
    setActiveIndex(idx);
    setTimeout(() => playerRef.current?.seekTo(within), 120);
  };

  const noVideo = playlist.length === 0 || !playlist[0]?.url || playlist[0].url.includes("/manus-storage");

  return (
    <Card data-testid="film-player-block">
      <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 space-y-0">
        <div className="min-w-0">
          <CardTitle className="text-base truncate flex items-center gap-2"><Clapperboard className="h-4 w-4 text-primary" /> {film.title}</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">{camera?.name} · {formatDuration(film.durationSec)} · {film.clipsCount} clipuri · {formatInTz(film.rangeStart, tz)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canDownload && <Button size="sm" variant="outline" className="bg-background" onClick={onDownload} disabled={concatLoading}>{concatLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}Descarcă MP4</Button>}
          {isAdmin && film.isAuto && <Button size="sm" variant="outline" className="bg-background" onClick={onRegenerate}><RefreshCcw className="h-3.5 w-3.5 mr-1" />Regenerează</Button>}
          {canDelete && <Button size="sm" variant="outline" className="bg-background text-destructive hover:text-destructive" onClick={onDelete}><Trash2 className="h-3.5 w-3.5 mr-1" />Șterge</Button>}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="aspect-video w-full rounded-lg overflow-hidden border bg-black">
          {noVideo ? (
            <div className="w-full h-full flex flex-col items-center justify-center text-center text-white/70 gap-2 p-6">
              <Film className="h-8 w-8" />
              <p className="text-sm max-w-md">Fișierele video ale acestui film au fost create în sistemul anterior (Manus) și nu sunt transferabile. Structura, evenimentele și linia temporală sunt păstrate.</p>
              <p className="text-xs max-w-md text-white/50">Ca să devină redabil: mergi la <strong>Încărcare arhive</strong>, alege acest șantier + cameră și reîncarcă arhiva zilei. Clipurile noi le înlocuiesc automat pe cele indisponibile.</p>
            </div>
          ) : (
            <ContinuousFilmPlayer ref={playerRef} playlist={playlist} activeIndex={activeIndex} onIndexChange={setActiveIndex} flip={!!camera?.flipVideo} />
          )}
        </div>
        {playlist.length > 0 && <FilmTimeline playlist={playlist} activeIndex={activeIndex} onSelect={setActiveIndex} timezone={tz} events={events} onEventClick={(ev) => seekToOffset(ev.filmOffsetSec)} />}
      </CardContent>
    </Card>
  );
}

function RangeDialog({ open, setOpen, projectId, cameraId, tz, onSubmit, pending }) {
  const [title, setTitle] = useState("");
  const [startVal, setStartVal] = useState("");
  const [endVal, setEndVal] = useState("");
  const submit = () => {
    if (!projectId || !cameraId) { toast.error("Selectați proiectul și camera în filtre"); return; }
    if (!startVal || !endVal) { toast.error("Completați intervalul"); return; }
    const startMs = new Date(startVal).getTime();
    const endMs = new Date(endVal).getTime();
    if (endMs <= startMs) { toast.error("Intervalul de sfârșit trebuie să fie după cel de început"); return; }
    onSubmit({ projectId: Number(projectId), cameraId: Number(cameraId), startMs, endMs, title: title || undefined });
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle>Extrage interval (film manual)</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5"><Label>Titlu (opțional)</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Turnare fundație" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Început</Label><Input type="datetime-local" value={startVal} onChange={(e) => setStartVal(e.target.value)} /></div>
            <div className="grid gap-1.5"><Label>Sfârșit</Label><Input type="datetime-local" value={endVal} onChange={(e) => setEndVal(e.target.value)} /></div>
          </div>
          <p className="text-xs text-muted-foreground">Se vor include toate clipurile din interval, în ordine cronologică.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="bg-background">Anulează</Button>
          <Button onClick={submit} disabled={pending}>{pending ? "Se creează..." : "Creează extras"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
